import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  formatPropChanged,
  formatNameSelector,
  formatPropsSelector,
  formatChanged,
  getHumanReadableFormat
} from 'redux/schemaSlice';
import useSchemaRequest from 'hooks/useSchemaRequest';

import {
  FormatSettingsContainer,
  StyledFormControl,
  StyledDeriveButton
} from './FormatSettings-styled';
import { Form, FormControl } from 'components/StyledComponents';

import {
  CalciteCheckbox,
  CalciteInput,
  CalciteLabel,
  CalciteOption,
  CalciteSelect
} from 'components/Calcite';

const KNOWN_FORMATS: FormatName[] = [
  'geo-json',
  'esri-json',
  'xml',
  'delimited',
  'json',
  'geo-rss-format',
  'rss-format'
];

const FormatSettings: React.FC = () => {
  const dispatch = useDispatch();
  const format = useSelector(formatNameSelector);
  const formatProps = useSelector(formatPropsSelector);
  const { requestSchema, isLoading } = useSchemaRequest();

  const onSchemaRequest = async () => {
    try {
      await requestSchema();
    } catch {}
  };

  const delimitedSettings = (
    <>
      <FormControl>
        <CalciteLabel>Field delimiter</CalciteLabel>
        <CalciteInput
          value={formatProps.fieldDelimiter || ''}
          onChange={value =>
            dispatch(formatPropChanged({ fieldDelimiter: value }))
          }
        />
      </FormControl>
      <StyledFormControl>
        <CalciteLabel layout="inline">
          <CalciteCheckbox
            checked={!!formatProps.hasHeaderRow}
            onChange={checked =>
              dispatch(formatPropChanged({ hasHeaderRow: checked }))
            }
          />
          Has header row
        </CalciteLabel>
      </StyledFormControl>
    </>
  );

  const jsonXMLSettings = (
    <>
      <FormControl>
        <CalciteLabel>Root node</CalciteLabel>
        <CalciteInput
          value={formatProps.rootNode || ''}
          onChange={value => dispatch(formatPropChanged({ rootNode: value }))}
        />
      </FormControl>
      <StyledFormControl>
        <CalciteLabel layout="inline">
          <CalciteCheckbox
            checked={!!formatProps.flatten}
            onChange={checked =>
              dispatch(formatPropChanged({ flatten: checked }))
            }
          />
          Flatten
        </CalciteLabel>
      </StyledFormControl>
      {formatProps.flatten && (
        <FormControl>
          <CalciteLabel>Flattening exemptions</CalciteLabel>
          <CalciteInput
            value={formatProps.fieldFlatteningExemptions || ''}
            onChange={value =>
              dispatch(
                formatPropChanged({
                  fieldFlatteningExemptions: value
                })
              )
            }
          />
        </FormControl>
      )}
    </>
  );

  const renderFormatSettings = () => {
    switch (format) {
      case 'delimited':
        return delimitedSettings;
      case 'xml':
      case 'json':
        return jsonXMLSettings;
    }
  };

  return (
    <FormatSettingsContainer>
      <Form horizontal>
        <FormControl>
          <CalciteLabel>Data format</CalciteLabel>
          <CalciteSelect
            label="Data format"
            onChange={value => dispatch(formatChanged(value as FormatName))}
            value={format}
          >
            {[
              <CalciteOption
                key="Unknown"
                selected={format === 'Unknown'}
                value="Unknown"
              >
                Auto Detect
              </CalciteOption>,
              ...KNOWN_FORMATS.map(knownFormat => (
                <CalciteOption
                  key={knownFormat}
                  selected={format === knownFormat}
                  value={knownFormat}
                >
                  {getHumanReadableFormat(knownFormat)}
                </CalciteOption>
              ))
            ]}
          </CalciteSelect>
        </FormControl>
        {renderFormatSettings()}
      </Form>
      <StyledDeriveButton
        onClick={onSchemaRequest}
        disabled={isLoading}
        loading={isLoading}
      >
        Derive
      </StyledDeriveButton>
    </FormatSettingsContainer>
  );
};

export default FormatSettings;
